import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FaTrashAlt,
  FaShoppingCart,
  FaArrowRight,
  FaCheckCircle,
  FaTag,
  FaCcVisa,
  FaCcMastercard,
  FaCcAmex,
  FaShieldAlt,
  FaTruck,
  FaGift,
  FaTimes,
  FaPercent,
  FaRupeeSign,
  FaChevronRight,
  FaFire,
  FaTicketAlt,
  FaLock,
  FaBolt,
  FaMinus,
  FaPlus,
} from "react-icons/fa";
import { MdLocalOffer, MdVerified } from "react-icons/md";
import { RiShoppingBag3Fill } from "react-icons/ri";
import { useApp } from "../App";
import api from "../services/api";
import { BASE_URL } from "../config";
import "../styles/CartPage.css";

export default function CartPage() {
  const navigate = useNavigate();
  const { cart, removeFromCart, updateQty, cartTotal, showToast } = useApp();
  const [coupon, setCoupon] = useState("");
  const [discount, setDiscount] = useState(0);
  const [discountAmt, setDiscountAmt] = useState(0);
  const [validating, setValidating] = useState(false);
  const [availableCoupons, setAvailableCoupons] = useState([]);
  const [loadingCoupons, setLoadingCoupons] = useState(false);
  const [appliedCouponCode, setAppliedCouponCode] = useState("");
  const [removingKey, setRemovingKey] = useState(null);
  const [bumpKey, setBumpKey] = useState(null);
  const [couponPanelOpen, setCouponPanelOpen] = useState(false);
  const [pulseTotal, setPulseTotal] = useState(false);

  const FREE_SHIPPING_THRESHOLD = 1000;
  const shipping = cartTotal >= FREE_SHIPPING_THRESHOLD ? 0 : 80;
  const total = cartTotal - discountAmt + shipping;
  const progressPct = Math.min(
    100,
    (cartTotal / FREE_SHIPPING_THRESHOLD) * 100,
  );
  const remaining = Math.max(0, FREE_SHIPPING_THRESHOLD - cartTotal);

  useEffect(() => {
    fetchAvailableCoupons();
  }, []);

  // Pulse total on change
  useEffect(() => {
    setPulseTotal(true);
    const t = setTimeout(() => setPulseTotal(false), 400);
    return () => clearTimeout(t);
  }, [total, discountAmt]);

  const fetchAvailableCoupons = async () => {
    setLoadingCoupons(true);
    try {
      const coupons = await api.coupons.getActive();
      setAvailableCoupons(coupons);
    } catch {
      setAvailableCoupons([
        {
          code: "WELCOME10",
          discountType: "percentage",
          discountValue: 10,
          minOrderValue: 500,
          description: "10% off on first order",
        },
        {
          code: "SAVE20",
          discountType: "percentage",
          discountValue: 20,
          minOrderValue: 1000,
          description: "20% off on orders above ₹1000",
        },
        {
          code: "FLAT100",
          discountType: "fixed",
          discountValue: 100,
          minOrderValue: 500,
          description: "Flat ₹100 off",
        },
      ]);
    } finally {
      setLoadingCoupons(false);
    }
  };

  const applyCoupon = async (code = null) => {
    const codeToApply = (code || coupon).trim().toUpperCase();
    if (!codeToApply) return;
    setValidating(true);
    try {
      const data = await api.coupons.validate(codeToApply, cartTotal);
      setDiscount(data.discountValue);
      setDiscountAmt(data.discountAmount);
      setAppliedCouponCode(codeToApply);
      setCouponPanelOpen(false);
      showToast(
        `Coupon "${codeToApply}" applied! You save ₹${data.discountAmount}.`,
      );
      setCoupon("");
    } catch (err) {
      setDiscount(0);
      setDiscountAmt(0);
      setAppliedCouponCode("");
      showToast(err.message || "Invalid coupon code.", "error");
    } finally {
      setValidating(false);
    }
  };

  const removeCoupon = () => {
    setDiscount(0);
    setDiscountAmt(0);
    setAppliedCouponCode("");
    showToast("Coupon removed.");
  };

  const handleRemove = (key) => {
    setRemovingKey(key);
    setTimeout(() => {
      removeFromCart(key);
      setRemovingKey(null);
    }, 380);
  };

  const handleQtyChange = (key, newQty) => {
    setBumpKey(key);
    updateQty(key, newQty);
    setTimeout(() => setBumpKey(null), 300);
  };

  /* ── EMPTY CART ── */
  if (cart.length === 0)
    return (
      <div className="cpx-page cpx-page--empty">
        <div className="cpx-container">
          <div className="cpx-empty-card">
            <div className="cpx-empty-icon-ring">
              <RiShoppingBag3Fill className="cpx-empty-icon" />
            </div>
            <h2 className="cpx-empty-title">Your cart is empty</h2>
            <p className="cpx-empty-sub">
              Discover pieces you'll love and add them to your cart.
            </p>
            <Link className="cpx-cta-btn" to="/collection">
              Shop Now <FaArrowRight />
            </Link>
          </div>
        </div>
      </div>
    );

  return (
    <div className="cpx-page">
      <div className="cpx-container">
        {/* Breadcrumb */}
        <nav className="cpx-breadcrumb">
          <Link to="/">Home</Link>
          <FaChevronRight className="cpx-bc-sep" />
          <span className="cpx-bc-active">Cart</span>
        </nav>

        <div className="cpx-layout">
          {/* ── LEFT: ITEMS ── */}
          <div className="cpx-left">
            <div className="cpx-section-head">
              <h1 className="cpx-title">
                Shopping Cart
                <span className="cpx-badge">{cart.length}</span>
              </h1>
            </div>

            <div className="cpx-items">
              {cart.map((item) => (
                <div
                  key={item.key}
                  className={`cpx-item ${removingKey === item.key ? "cpx-item--removing" : "cpx-item--visible"}`}
                >
                  {/* Image */}
                  <div
                    className="cpx-item-img"
                    onClick={() => navigate(`/product/${item.productId}`)}
                  >
                    <img
                      src={`${BASE_URL}${item.image}`}
                      alt={item.name}
                      onError={(e) => {
                        e.target.src = `https://placehold.co/100x130/f0e8dc/8B1A1A?text=IMG`;
                      }}
                    />
                    <div className="cpx-item-img-overlay">
                      <FaArrowRight />
                    </div>
                  </div>

                  {/* Details */}
                  <div className="cpx-item-body">
                    <div className="cpx-item-top">
                      <div>
                        <p className="cpx-item-brand">{item.brand}</p>
                        <h3
                          className="cpx-item-name"
                          onClick={() => navigate(`/product/${item.productId}`)}
                        >
                          {item.name}
                        </h3>
                        <div className="cpx-item-tag">
                          <span>Size: {item.size}</span>
                        </div>
                      </div>
                      <div className="cpx-item-price">
                        ₹{(item.price * item.qty).toLocaleString()}
                      </div>
                    </div>

                    <div className="cpx-item-foot">
                      {/* Qty */}
                      <div className="cpx-qty">
                        <button
                          className="cpx-qty-btn"
                          onClick={() =>
                            handleQtyChange(item.key, item.qty - 1)
                          }
                          disabled={item.qty <= 1}
                        >
                          <FaMinus />
                        </button>
                        <span
                          className={`cpx-qty-val ${bumpKey === item.key ? "cpx-qty-bump" : ""}`}
                        >
                          {item.qty}
                        </span>
                        <button
                          className="cpx-qty-btn"
                          onClick={() =>
                            handleQtyChange(item.key, item.qty + 1)
                          }
                        >
                          <FaPlus />
                        </button>
                      </div>

                      {/* Unit price */}
                      <span className="cpx-unit-price">
                        ₹{item.price.toLocaleString()} each
                      </span>

                      {/* Remove */}
                      <button
                        className="cpx-remove-btn"
                        onClick={() => handleRemove(item.key)}
                        aria-label="Remove"
                      >
                        <FaTrashAlt />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Trust Badges */}
            <div className="cpx-trust-row">
              <div className="cpx-trust-item">
                <FaTruck className="cpx-trust-icon" />
                <span>Fast Delivery</span>
              </div>
              <div className="cpx-trust-item">
                <FaShieldAlt className="cpx-trust-icon" />
                <span>Secure Payments</span>
              </div>
              <div className="cpx-trust-item">
                <MdVerified className="cpx-trust-icon" />
                <span>100% Authentic</span>
              </div>
              <div className="cpx-trust-item">
                <FaGift className="cpx-trust-icon" />
                <span>Gift Wrapping</span>
              </div>
            </div>
          </div>

          {/* ── RIGHT: SUMMARY ── */}
          <div className="cpx-right">
            <div className="cpx-summary-card">
              <h3 className="cpx-summary-title">Order Summary</h3>

              {/* Shipping Progress */}
              <div className="cpx-ship-box">
                <div className="cpx-ship-top">
                  <FaTruck
                    className={`cpx-ship-icon ${remaining === 0 ? "cpx-ship-icon--free" : ""}`}
                  />
                  {remaining > 0 ? (
                    <p className="cpx-ship-text">
                      Add <strong>₹{remaining.toLocaleString()}</strong> more
                      for <strong>FREE shipping</strong>
                    </p>
                  ) : (
                    <p className="cpx-ship-free">
                      <FaCheckCircle /> You've unlocked FREE shipping!
                    </p>
                  )}
                </div>
                <div className="cpx-progress-track">
                  <div
                    className="cpx-progress-fill"
                    style={{ width: `${progressPct}%` }}
                  />
                </div>
                <div className="cpx-progress-labels">
                  <span>₹0</span>
                  <span>₹{FREE_SHIPPING_THRESHOLD.toLocaleString()}</span>
                </div>
              </div>

              {/* Coupon Section */}
              <div className="cpx-coupon-wrap">
                {appliedCouponCode ? (
                  <div className="cpx-applied-coupon">
                    <div className="cpx-applied-left">
                      <FaTicketAlt className="cpx-applied-icon" />
                      <div>
                        <span className="cpx-applied-code">
                          {appliedCouponCode}
                        </span>
                        <span className="cpx-applied-save">
                          Saving ₹{discountAmt.toLocaleString()}
                        </span>
                      </div>
                    </div>
                    <button
                      className="cpx-remove-coupon"
                      onClick={removeCoupon}
                    >
                      <FaTimes />
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="cpx-coupon-row">
                      <div className="cpx-coupon-field">
                        <MdLocalOffer className="cpx-coupon-ico" />
                        <input
                          className="cpx-coupon-input"
                          type="text"
                          placeholder="Enter coupon code"
                          value={coupon}
                          onChange={(e) => setCoupon(e.target.value)}
                          onKeyDown={(e) => e.key === "Enter" && applyCoupon()}
                        />
                      </div>
                      <button
                        className="cpx-apply-btn"
                        onClick={() => applyCoupon()}
                        disabled={validating || !coupon.trim()}
                      >
                        {validating ? (
                          <span className="cpx-spinner" />
                        ) : (
                          "Apply"
                        )}
                      </button>
                    </div>

                    {/* Offers Toggle */}
                    <button
                      className="cpx-offers-toggle"
                      onClick={() => setCouponPanelOpen((p) => !p)}
                    >
                      <FaBolt className="cpx-offers-ico" />
                      {availableCoupons.length} offers available
                      <FaChevronRight
                        className={`cpx-offers-arrow ${couponPanelOpen ? "cpx-offers-arrow--open" : ""}`}
                      />
                    </button>

                    {couponPanelOpen && (
                      <div className="cpx-offers-panel">
                        {loadingCoupons ? (
                          <div className="cpx-offers-loading">
                            <span className="cpx-spinner cpx-spinner--dark" />{" "}
                            Loading offers...
                          </div>
                        ) : (
                          availableCoupons.map((c, i) => (
                            <div
                              key={i}
                              className="cpx-offer-card"
                              style={{ animationDelay: `${i * 60}ms` }}
                              onClick={() => applyCoupon(c.code)}
                            >
                              <div className="cpx-offer-left">
                                <span className="cpx-offer-code">{c.code}</span>
                                <span className="cpx-offer-desc">
                                  {c.description}
                                </span>
                                {c.minOrderValue > 0 && (
                                  <span className="cpx-offer-min">
                                    Min. ₹{c.minOrderValue}
                                  </span>
                                )}
                              </div>
                              <div className="cpx-offer-right">
                                <span className="cpx-offer-discount">
                                  {c.discountType === "percentage" ? (
                                    <>
                                      <FaPercent /> {c.discountValue}% OFF
                                    </>
                                  ) : (
                                    <>
                                      <FaRupeeSign /> {c.discountValue} OFF
                                    </>
                                  )}
                                </span>
                                <button className="cpx-offer-apply">
                                  Apply →
                                </button>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="cpx-divider" />

              {/* Price Breakdown */}
              <div className="cpx-price-rows">
                <div className="cpx-price-row">
                  <span>Subtotal ({cart.length} items)</span>
                  <span>₹{cartTotal.toLocaleString()}</span>
                </div>
                {discountAmt > 0 && (
                  <div className="cpx-price-row cpx-price-row--discount">
                    <span>
                      <FaPercent style={{ fontSize: 10, marginRight: 4 }} />
                      Coupon Discount
                    </span>
                    <span>−₹{discountAmt.toLocaleString()}</span>
                  </div>
                )}
                <div className="cpx-price-row">
                  <span>Shipping</span>
                  <span>
                    {shipping === 0 ? (
                      <span className="cpx-free-chip">FREE</span>
                    ) : (
                      `₹${shipping}`
                    )}
                  </span>
                </div>
              </div>

              <div className="cpx-divider" />

              <div
                className={`cpx-total-row ${pulseTotal ? "cpx-total-row--pulse" : ""}`}
              >
                <span>Total</span>
                <span>₹{total.toLocaleString()}</span>
              </div>

              {discountAmt > 0 && (
                <div className="cpx-savings-strip">
                  <FaFire /> You're saving ₹{discountAmt.toLocaleString()} on
                  this order!
                </div>
              )}

              <button
                className="cpx-checkout-btn"
                onClick={() =>
                  navigate("/checkout", {
                    state: {
                      discountAmt,
                      couponCode: appliedCouponCode,
                      discount,
                    },
                  })
                }
              >
                <FaLock style={{ fontSize: 14 }} />
                Proceed to Checkout
                <FaArrowRight style={{ fontSize: 14 }} />
              </button>

              <p className="cpx-secure-note">
                <FaShieldAlt /> 256-bit SSL encrypted & secure
              </p>

              <div className="cpx-payments">
                <FaCcVisa title="Visa" />
                <FaCcMastercard title="Mastercard" />
                <FaCcAmex title="Amex" />
                <span className="cpx-upi-badge">UPI</span>
                <span className="cpx-upi-badge">COD</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
